import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Modal } from 'bootstrap';

import { ChartOptions } from 'chart.js';
import { Subscription, take } from 'rxjs';

import { Diet, Meal } from 'src/app/interfaces/diet';
import { FoodOption } from 'src/app/interfaces/food-option';

import { DietService } from 'src/app/services/diet.service';
import { FoodOptionService } from 'src/app/services/food-option.service';
import { StateService } from 'src/app/services/state.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-diet-detail',
  templateUrl: './diet-detail.component.html',
  styleUrls: ['./diet-detail.component.scss'],
})
export class DietDetailComponent implements OnInit {
  @Input() diet?: Diet;
  @Input() isMobile: boolean = false;
  @Input() userId?: number;

  @Output() getUserDietsHistory = new EventEmitter<any>();

  editingDiet: boolean = false;

  mealToEdit?: Meal;
  mealMode: 'creating' | 'editing' | 'watching' = 'watching';
  mealForm: FormGroup;

  // Chart.js
  public options: ChartOptions<'pie'> = {
    responsive: true,
  };
  public labels = [['Grasas Totales'], ['Proteínas'], ['Carbohidratos']];
  public datasets = [
    {
      data: [0, 0, 0],
      backgroundColor: [
        'rgb(237, 109, 133)',
        'rgb(87, 160, 229)',
        'rgb(247, 207, 107)',
      ],
    },
  ];
  public legend = true;
  public plugins = [];

  subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private stateService: StateService,
    private dietService: DietService,
    private toastService: ToastService,
    private foodOptionService: FoodOptionService
  ) {
    this.mealForm = this.fb.group({
      title: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.getMealsFromDiet();
  }

  openCalendarModal() {
    this.stateService.selectedDiet = this.diet;

    if (this.stateService.openModal) {
      this.stateService.openModal.hide();
    }

    const dietCalendarModal = new Modal('#dietCalendarModal');
    this.stateService.openModal = dietCalendarModal;

    dietCalendarModal.show();
  }

  getDietProgretion(diet: Diet) {
    diet.loading = true;

    this.dietService
      .getDietProgretion(diet.id, this.userId as number)
      .subscribe({
        next: () => {
          this.toastService.success(
            'Te enviamos la progresión de la dieta a tu email.'
          );
          diet.loading = false;
        },
        error: () => {
          diet.loading = false;
        },
      });
  }

  finishOrDeleteDiet(diet: Diet) {
    this.subs.add(
      this.dietService.finishDiet(diet.id).subscribe({
        next: (_response) => {
          this.getUserDietsHistory.emit();

          this.toastService.success('Haz finalizado la dieta con éxito');
        },
        error: (_error) => {
          this.toastService.alert(
            'Ha ocurrido un problema finalizando tu dieta.'
          );
        },
      })
    );
  }

  finishDiet() {
    if (!this.diet) return;

    if (this.diet.status == 'CREATED' || this.diet.status == 'NOT_STARTED') {
      if (
        window.confirm(
          'Ya que no hay registros o no se inicio la dieta vamos a eliminar esta dieta en vez de finalizarla. ¿Estás seguro que quieres eliminarla?'
        )
      ) {
        this.finishOrDeleteDiet(this.diet);
      }
    } else {
      this.finishOrDeleteDiet(this.diet);
    }
  }

  toggleEditing(meal: Meal | undefined) {
    if (meal) {
      if (this.mealToEdit) {
        this.mealToEdit.selected = false;
      }

      this.mealMode = 'editing';
      this.mealToEdit = meal;
      meal.selected = true;

      this.title.setValue(meal.title);
    } else {
      if (this.mealToEdit) {
        this.mealToEdit.selected = false;
      }

      this.mealMode = 'creating';
      this.mealToEdit = undefined;
      this.clearForm();
    }
  }

  getMealsFromDiet() {
    if (!this.diet) return;

    this.dietService
      .getMealsFromDiet(this.diet.id)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (!this.diet) return;

          if (this.diet.status === 'FINISHED') {
            this.title.disable();
          } else {
            this.title.enable();
          }

          if (response.meals) {
            response.meals.sort((a: Meal, b: Meal) => a.order - b.order);
          }

          this.diet.meals = response.meals;
          this.diet.macros = response.macros;

          if (this.diet.meals) {
            this.diet.meals.forEach((meal: Meal) => {
              if (!meal.foodOptions) {
                meal.foodOptions = [];
              }
            });
          }

          if (this.mealToEdit) {
            this.mealToEdit = this.diet.meals.filter(
              (meal: Meal) => meal.id == this.mealToEdit!.id
            )[0];
          }

          this.datasets = [
            {
              data: [
                this.diet?.macros?.fatsKcalPercentage as number,
                this.diet?.macros?.proteinsKcalPercentage as number,
                this.diet?.macros?.carbohydratesKcalPercentage as number,
              ],

              backgroundColor: [
                'rgb(237, 109, 133)',
                'rgb(87, 160, 229)',
                'rgb(247, 207, 107)',
              ],
            },
          ];
        },
        error: () => {
          this.toastService.alert(
            'Ha ocurrido un problema encontrando las comidas en la base de datos.'
          );
        },
      });
  }

  selectMeal(meal: Meal) {
    this.mealToEdit = meal;
  }

  deleteMeal() {
    if (!window.confirm('¿Seguro que quieres borrar esta comida?')) {
      return;
    }

    this.subs.add(
      this.dietService.deleteMeal(this.mealToEdit!).subscribe({
        next: (response: any) => {
          this.getMealsFromDiet();
          this.clearForm();
          this.mealMode = 'watching';
          this.mealToEdit = undefined;
          this.toastService.success('Se pudo eliminar la comida con éxito.');
        },
        error: (_error) => {
          this.toastService.alert('No pudo eliminar la comida con éxito.');
        },
      })
    );
  }

  createMeal() {
    let mealOrder: number;

    if (this.diet!.meals) {
      const lastMealIndex = this.diet!.meals.length - 1;
      mealOrder = this.diet!.meals[lastMealIndex].order + 1;
    } else {
      mealOrder = 1;
    }

    const meal: Pick<Meal, 'dietId' | 'title' | 'description' | 'order'> = {
      title: this.title.value,
      description: '',
      dietId: this.diet!.id,
      order: mealOrder,
    };

    this.dietService
      .createMeal(meal)
      .pipe(take(1))
      .subscribe({
        next: (meal: Meal) => {
          this.clearForm();
          this.toastService.success('Se creó la comida con éxito.');

          this.mealToEdit = meal;

          if (!this.diet!.meals) {
            this.diet!.meals = [meal];
          } else {
            this.diet?.meals.push(meal);
          }

          this.getMealsFromDiet();

          this.toggleEditing(this.diet?.meals.at(-1));
        },
        error: (_error) => {
          this.toastService.alert(
            'Ha ocurrido un error creando la comida. Si el error persiste contactarse con el soporte.'
          );
        },
      });
  }

  editMeal() {
    const meal: Meal = {
      ...this.mealToEdit!,
      title: this.title.value,
    };

    this.dietService
      .updateMeal(meal)
      .pipe(take(1))
      .subscribe({
        next: (meal: Meal) => {
          this.toastService.success('Se editó la comida con éxito.');

          this.getMealsFromDiet();
        },
        error: (_error) => {
          this.toastService.alert(
            'Ha ocurrido un error creando la comida. Si el error persiste contactarse con el soporte.'
          );
        },
      });
  }

  openFoodOptionModal(foodOptionGroup: FoodOption[] | undefined) {
    const foodOptionModal = new Modal('#foodOptionsFormModal');
    const foodOptionModalHTMLElement = document.getElementById(
      'foodOptionsFormModal'
    );

    this.stateService.openModal = foodOptionModal;

    if (foodOptionGroup) {
      this.stateService.selectedFoodGroup = foodOptionGroup[0].group;
    }

    foodOptionModalHTMLElement?.addEventListener('hidden.bs.modal', () => {
      this.stateService.selectedFoodGroup = undefined;
      this.getMealsFromDiet();
    });

    foodOptionModal.show();
  }

  setDefaultFoodOption(foodOption: FoodOption, foodOptionGroup: FoodOption[]) {
    if (this.diet!.status === 'FINISHED') {
      return;
    }

    if (!window.confirm('¿Seguro que quieres cambiar el predeterminado?')) {
      return;
    }

    const previousDefault = foodOptionGroup.filter(
      (fo: FoodOption) => fo.isDefault
    )[0];

    previousDefault.isDefault = false;

    foodOption.isDefault = true;

    this.subs.add(
      this.foodOptionService.updateFood(previousDefault).subscribe({
        next: () => {
          this.getMealsFromDiet();
        },
      })
    );

    this.subs.add(
      this.foodOptionService.updateFood(foodOption).subscribe({
        next: () => {
          this.getMealsFromDiet();
        },
      })
    );
  }

  manageMealSubmit() {
    if (this.mealMode == 'creating') {
      this.createMeal();
    } else {
      this.editMeal();
    }
  }

  cancelForm() {
    this.clearForm();
    this.mealMode = 'watching';
    this.mealToEdit!.selected = false;
    this.mealToEdit = undefined;
  }

  clearForm() {
    this.title.setValue('');
    this.mealForm.reset();
  }

  deleteFoodOption(
    foodOption: FoodOption,
    foodOptionGroup: FoodOption[],
    event: Event
  ) {
    event.stopPropagation();

    if (foodOptionGroup.length > 1 && foodOption.isDefault) {
      this.toastService.alert(
        'No puedes eliminar la comida predeterminada de un grupo, primero debes cambiar la comida predeterminada.'
      );
      return;
    }

    if (
      !window.confirm(
        '¿Seguro que quieres borrar esta comida? No podras recuperarla.'
      )
    ) {
      return;
    }

    this.subs.add(
      this.foodOptionService.deleteFood(foodOption.id!).subscribe({
        next: () => {
          this.getMealsFromDiet();
        },
      })
    );
  }

  selectFoodOption(foodOption: FoodOption, event: Event) {
    event.stopPropagation();

    this.stateService.selectedFoodOption = foodOption;
    this.stateService.updateEdition(true);

    this.openFoodOptionModal(undefined);
  }

  openImportUserSelectorModal() {
    const importUserSelectorModal = new Modal('#importUserSelectorModal');
    this.stateService.openModal = importUserSelectorModal;

    this.stateService.selectedDiet = this.diet;

    importUserSelectorModal.show();
  }

  onDietCreate() {
    this.getUserDietsHistory.emit();
  }

  get title() {
    return this.mealForm.controls['title'];
  }

  translateDaysOfWeek(days: string[]): string {
    if (!days || days.length === 0) return 'Todos los días';

    const dayTranslations: { [key: string]: string } = {
      MONDAY: 'Lunes',
      TUESDAY: 'Martes',
      WEDNESDAY: 'Miércoles',
      THURSDAY: 'Jueves',
      FRIDAY: 'Viernes',
      SATURDAY: 'Sábado',
      SUNDAY: 'Domingo',
    };

    return days.map((day) => dayTranslations[day] || day).join(', ');
  }
}
